import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useProfile, useMeeting } from "@hooks";
import { emitChatMessage } from "@socket";
import {
  ownMessagePending,
  ownMessageConfirmed,
  ownMessageFailed,
  ownMessageRetrying,
  ownMessageDiscarded,
} from "@store/chatSlice";

const useChat = () => {
  const dispatch = useDispatch();
  const { profile, profileId } = useProfile();
  const { meetingInstanceId } = useMeeting();

  const deliverMessage = useCallback(
    async ({ clientMessageId, text }) => {
      try {
        const acknowledgement = await emitChatMessage({
          meetingInstanceId,
          clientMessageId,
          text,
        });

        if (acknowledgement?.ok) {
          dispatch(
            ownMessageConfirmed({
              clientMessageId,
              message: acknowledgement.message,
            })
          );
        } else {
          dispatch(
            ownMessageFailed({
              clientMessageId,
              errorCode: acknowledgement?.code,
            })
          );
        }
      } catch (error) {
        dispatch(ownMessageFailed({ clientMessageId }));
      }
    },
    [dispatch, meetingInstanceId]
  );

  const sendMessage = useCallback(
    (rawText) => {
      const text = rawText.trim();
      if (!text || !meetingInstanceId) return;

      const clientMessageId = uuidv4();
      dispatch(
        ownMessagePending({
          clientMessageId,
          senderId: profileId,
          senderName: profile?.username || profile?.firstName,
          senderColor: profile?.color,
          senderAvatar: profile?.avatar,
          text,
          sentAt: Date.now(),
        })
      );

      deliverMessage({ clientMessageId, text });
    },
    [dispatch, deliverMessage, profile, profileId, meetingInstanceId]
  );

  const retryMessage = useCallback(
    (message) => {
      dispatch(
        ownMessageRetrying({ clientMessageId: message.clientMessageId })
      );
      deliverMessage({
        clientMessageId: message.clientMessageId,
        text: message.text,
      });
    },
    [dispatch, deliverMessage]
  );

  const discardMessage = useCallback(
    (clientMessageId) => {
      dispatch(ownMessageDiscarded({ clientMessageId }));
    },
    [dispatch]
  );

  return { sendMessage, retryMessage, discardMessage };
};

export default useChat;
